import { HOSTNAME_RE } from './manifest.js';

/**
 * Generated hostnames (FR-NET-01). A VPS may have a domain (e.g. eu1.devrobot.dev); applications
 * deployed there get `<label>.<domain>`, where the label comes from a platform-wide template.
 * The label is always a single DNS label, so one wildcard record (*.<domain>) covers every app.
 */
export const HOSTNAME_TEMPLATE_DEFAULT = 'dr-{project}-{environment}';
export const HOSTNAME_TEMPLATE_TOKENS = ['project', 'environment', 'service'] as const;
export type HostnameTemplateVars = { project: string; environment: string; service?: string };

const TOKEN_RE = /\{(project|environment|service)\}/g;

/** Returns a human-readable problem with the template, or null when it is valid. */
export function validateHostnameTemplate(template: string): string | null {
  const t = template.trim();
  if (!t) return 'The hostname template is empty.';
  if (t.length > 100) return 'The hostname template is longer than 100 characters.';
  if (t.includes('.')) return 'The hostname template is a single DNS label, so it cannot contain dots (the VPS domain is appended).';
  const unknown = [...t.matchAll(/\{([^}]*)\}/g)].map((m) => m[1]).filter((n) => !(HOSTNAME_TEMPLATE_TOKENS as readonly string[]).includes(n));
  if (unknown.length) return `Unknown placeholder ${unknown.map((n) => `{${n}}`).join(', ')}. Use {project}, {environment} or {service}.`;
  if (!t.includes('{project}')) return 'The hostname template must contain {project}.';
  if (!/^[a-z0-9-]*$/.test(t.replace(TOKEN_RE, ''))) return 'Outside placeholders, the hostname template may only use lowercase letters, digits and dashes.';
  return null;
}

/** Validates and normalizes a VPS domain ("EU1.devrobot.dev." → "eu1.devrobot.dev"). */
export function parseDomain(input: string): { ok: true; domain: string } | { ok: false; error: string } {
  const d = input.trim().toLowerCase().replace(/\.$/, '');
  if (/^[a-z]+:\/\//.test(d) || d.includes('/')) return { ok: false, error: `"${input}" is not a domain: use only the name, e.g. eu1.devrobot.dev (no scheme or path).` };
  if (d.startsWith('*.')) return { ok: false, error: 'Enter the domain without "*." — DevRobot adds one label per application under it.' };
  if (d.length > 189 || !HOSTNAME_RE.test(d)) return { ok: false, error: `"${input}" is not a valid domain name.` };
  return { ok: true, domain: d };
}

/** FNV-1a, 32 bit: a short stable suffix for labels that exceed 63 characters (not a security measure). */
function shortHash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0').slice(0, 6);
}

/** The DNS label produced by the template: lowercase, single dashes, at most 63 characters. */
export function hostnameLabel(template: string, vars: HostnameTemplateVars): string {
  const raw = template
    .replace(TOKEN_RE, (_, k: keyof HostnameTemplateVars) => vars[k] ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (raw.length <= 63) return raw || 'app';
  return `${raw.slice(0, 56).replace(/-+$/, '')}-${shortHash(raw)}`;
}

/** The generated hostname of an application on a VPS with a domain, e.g. dr-erp-development.eu1.devrobot.dev. */
export function defaultHostname(template: string, vars: HostnameTemplateVars, domain: string): string {
  return `${hostnameLabel(template, vars)}.${domain}`;
}
